import React, { useState, useEffect, useCallback } from 'react';
import { getAuditLogs } from '../services/adminService';
import { pageContainer, cardStyle, sectionTitle, tableSearch } from '../styles/theme';
import Paginator from '../components/Paginator';
import PageHeader from '../components/PageHeader';

const AdminLogs = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const data = await getAuditLogs({ search });
    setLogs(data || []);
    setLoading(false);
  }, [search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const paged = logs.slice((page - 1) * pageSize, page * pageSize);

  const getActionColor = (action: string) => {
    if (action.includes('CREATE')) return '#10b981';
    if (action.includes('DELETE') || action.includes('DEACTIVATE')) return '#ef4444';
    if (action.includes('UPDATE')) return '#3b82f6';
    if (action.includes('RESOLVE')) return '#8b5cf6';
    return '#64748b';
  };

  const formatDetails = (details: any) => {
    if (!details) return '';
    if (typeof details === 'string') return details;
    return Object.entries(details)
      .map(([key, val]) => `${key}: ${JSON.stringify(val)}`)
      .join(', ');
  };

  return (
    <div style={pageContainer}>
      <PageHeader
        breadcrumb="Dashboard > Admin Logs"
        title="Admin Audit Logs"
        subtitle="Track all administrative actions, CRUD operations, and system changes."
        onRefresh={fetchLogs}
      />

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
          <h3 style={{ ...sectionTitle, margin: 0 }}>Audit History</h3>
          <input
            type="text" 
            placeholder="Search by action, entity, ID…"
            style={{ ...tableSearch, minWidth: 300 }}
            value={search} 
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f1f5f9', textAlign: 'left' }}>
                <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>TIME</th>
                <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>ADMIN</th>
                <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>ACTION</th>
                <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>ENTITY</th>
                <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>DETAILS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading logs...</td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No audit logs found.</td>
                </tr>
              ) : (
                paged.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '14px' }}>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap', color: '#64748b' }}>
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{log.admin?.name || 'System'}</div>
                      <div style={{ fontSize: '12px', color: '#94a3b8' }}>{log.admin?.email || ''}</div>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        background: `${getActionColor(log.action)}15`, 
                        color: getActionColor(log.action),
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ fontWeight: 500 }}>{log.entity}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>ID: {log.entityId || 'N/A'}</div>
                    </td>
                    <td style={{ padding: '12px 8px', color: '#475569', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={formatDetails(log.details)}>
                      {formatDetails(log.details)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && logs.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <Paginator total={logs.length} page={page} pageSize={pageSize} onPage={setPage} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogs;
